import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
  Chip,
  TextField,
  Divider,
  Box,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import _ from "lodash";

import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import { DeliveryTpe } from "../../../../utils/enum";
import { DELIVERY_TYPE_DINEIN } from "../../../../utils/consts";
import TextfieldDefaultNew from "../../../../components/textField/TextfieldDefaultNew";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import AvailabilitySelection from "../../../../components/time/AvailabilitySelection";
import {
  isEqualArrayObject,
  isEqualArrays,
} from "../../../../utils/checkArrayEqual";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autocomplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.background.entity_border}`,
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

// Platform selector object
export const platformSelectorObj: any = {
  "walk in": { id: "walk in", label: "Walk In" },
  phone: { id: "phone", label: "Phone" },
  kiosk: { id: "kiosk", label: "Kiosk" },
  online: { id: "online", label: "Online" },
};

// Channel selector list
export const channelSelectorList = [
  { id: "eatpresto", label: "Eat Presto" },
  { id: "deliverect", label: "Deliverect" },
  { id: "justeat", label: "Just Eat" },
  { id: "deliveroo", label: "Deliveroo" },
  { id: "ubereats", label: "Uber Eats" },
];

interface PlatformOption {
  id: string;
  label: string;
}

interface ChannelOption {
  id: string;
  label: string;
}

export interface StrategyAddEditModalProps {
  isOpen: any;
  setIsOpen: any;
  setStrategyName: any;
  strategyName: any;
  deliveryTypesList: any;
  setDeliveryTypesList: any;
  handleSubmit: any;
  availability: any;
  handleSubmitAvailability: any;
  setAvailabilityErrorMessage: any;
  isDeliveryTypeEmpty: any;
  setIsNameEmpty: any;
  setIsDeliveryTypeEmpty: any;
  isNameEmpty: any;
  isAvailabilityEmpty: any;
  isLoadingButton: any;
  isEdit: any;
  isDuplicate: any;
  handleReset: any;
  deliveryTypesListInitial: any;
  availabilityInitial: any;
  strategyNameInitial: any;
  // New props for platforms and channels
  platformList?: any[];
  setPlatformList?: (value: any[]) => void;
  channelList?: any[];
  setChannelList?: (value: any[]) => void;
  platformListInitial?: any[];
  channelListInitial?: any[];
  // Props for edit mode data
  nodeData?: any;
}

/**
 * StrategyAddEditModal Component
 *
 * This component is responsible for rendering a modal dialog for adding, editing, or duplicating a strategy.
 * It includes fields for strategy name, delivery types (Dine In, Delivery, Takeout), and availability.
 * The user can select delivery types using switches and set availability using an availability selection component.
 * Additionally, the component provides options for canceling, saving, or creating the strategy.
 */
const StrategyAddEditModal: React.FunctionComponent<
  StrategyAddEditModalProps
> = ({
  isOpen,
  setIsOpen,
  setStrategyName,
  strategyName,
  deliveryTypesList,
  setDeliveryTypesList,
  handleSubmit,
  availability,
  handleSubmitAvailability,
  setAvailabilityErrorMessage,
  isDeliveryTypeEmpty,
  setIsNameEmpty,
  setIsDeliveryTypeEmpty,
  isNameEmpty,
  isAvailabilityEmpty,
  isLoadingButton,
  isEdit,
  isDuplicate,
  handleReset,
  deliveryTypesListInitial,
  availabilityInitial,
  strategyNameInitial,
  platformList = [],
  setPlatformList = () => {},
  channelList = [],
  setChannelList = () => {},
  platformListInitial = [],
  channelListInitial = [],
  nodeData,
}) => {
  const classes = useStyles();

  // Get all platform IDs
  const allPlatformIds = Object.values(platformSelectorObj).map(
    (p: any) => p.id,
  );
  const allChannelIds = channelSelectorList.map((c) => c.id);
  const allDeliveryTypes = [
    DeliveryTpe.DINEIN,
    DeliveryTpe.DELIVERY,
    DeliveryTpe.TAKEOUT,
  ];

  // ✅ Check if platform data exists and has values
  const hasPlatformData = () => {
    if (!nodeData) return false;
    if ("platform" in nodeData) {
      const platformData = nodeData.platform;
      return Array.isArray(platformData) && platformData.length > 0;
    }
    return false;
  };

  // ✅ Check if channel data exists and has values
  const hasChannelData = () => {
    if (!nodeData) return false;
    if ("orderChannel" in nodeData) {
      const channelData = nodeData.orderChannel;
      return Array.isArray(channelData) && channelData.length > 0;
    }
    return false;
  };

  // ✅ Function to determine if platforms should show all selected
  const shouldShowAllPlatforms = () => {
    // If platformList is empty AND no platform data exists, show all
    if (platformList.length === 0 && !hasPlatformData()) {
      return true;
    }
    // If platformList is empty but platformListInitial has data, don't show all
    if (platformList.length === 0 && platformListInitial.length > 0) {
      return false;
    }
    return false;
  };

  // ✅ Function to determine if channels should show all selected
  const shouldShowAllChannels = () => {
    // If channelList is empty AND no channel data exists, show all
    if (channelList.length === 0 && !hasChannelData()) {
      return true;
    }
    // If channelList is empty but channelListInitial has data, don't show all
    if (channelList.length === 0 && channelListInitial.length > 0) {
      return false;
    }
    return false;
  };

  // Convert platform list to objects with label
  const getPlatformObjects = (ids: string[]): PlatformOption[] => {
    // ✅ If no platforms selected and should show all, return all platforms
    if (ids.length === 0 && shouldShowAllPlatforms()) {
      return Object.values(platformSelectorObj);
    }
    return ids.map((id) => ({
      id,
      label: platformSelectorObj[id]?.label || id,
    }));
  };

  // Convert channel list to objects with label
  const getChannelObjects = (ids: string[]): ChannelOption[] => {
    // ✅ If no channels selected and should show all, return all channels
    if (ids.length === 0 && shouldShowAllChannels()) {
      return channelSelectorList;
    }
    return ids.map((id) => ({
      id,
      label: channelSelectorList.find((c) => c.id === id)?.label || id,
    }));
  };

  // Get platform objects for Autocomplete
  const platformOptions: PlatformOption[] = Object.values(platformSelectorObj);
  const selectedPlatforms: PlatformOption[] = getPlatformObjects(platformList);

  // Get channel objects for Autocomplete
  const channelOptions: ChannelOption[] = channelSelectorList;
  const selectedChannels: ChannelOption[] = getChannelObjects(channelList);

  // Handle platform change
  const handlePlatformChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: PlatformOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setPlatformList(selectedIds);
  };

  // Handle channel change
  const handleChannelChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: ChannelOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setChannelList(selectedIds);
  };

  /* Update the deliveryTypeList after selecting the delivery options using switches (dine-in, takeout, and delivery). */
  const handleSelectDeliveryTypes = (e: any) => {
    const { value, name, checked } = e.target;
    // When a delivery type option is selected, that delivery type option is entered into a list.
    if (checked) {
      const selectedList: any = new Set([...deliveryTypesList, value]);
      setDeliveryTypesList([...selectedList]);
    } else {
      // When a delivery type option is not selected, that delivery type option is removed into a list.
      const selectedList: any = deliveryTypesList.filter(
        (amountType: String) => name !== amountType,
      );
      setDeliveryTypesList(selectedList);
    }
    setIsDeliveryTypeEmpty(false);
  };

  /**
   * Function handleDisable
   *
   * This function determines whether the submit button should be disabled based on whether there are any changes in the form fields compared to their initial values.
   */
  const handleDisable = () => {
    // Check if the strategy name, delivery types list, and availability are equal to their initial values
    const isPlatformEqual = _.isEqual(platformList, platformListInitial);
    const isChannelEqual = _.isEqual(channelList, channelListInitial);

    if (
      strategyName === strategyNameInitial &&
      isEqualArrays(deliveryTypesList, deliveryTypesListInitial) &&
      isEqualArrayObject(
        availability.availability,
        availabilityInitial.availability,
      ) &&
      isPlatformEqual &&
      isChannelEqual
    ) {
      // If all values are equal, disable the submit button
      return true;
    } else {
      // If any value has changed, enable the submit button
      return false;
    }
  };

  return (
    <>
      <DialogCommonDefault
        open={isOpen}
        setOpen={setIsOpen}
        isNeedFixedHeight={false}
      >
        <DialogTitle>{`${
          isEdit ? "Edit" : isDuplicate ? "Duplicate" : "Add New"
        } Strategy`}</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={12}>
              <Typography style={{ paddingLeft: "4px" }}>Name</Typography>
              <TextfieldDefaultNew
                id="sign-password-input"
                name="sign-password-input"
                onChange={(e: any) => {
                  setIsNameEmpty("");
                  setStrategyName(e.target.value);
                }}
                value={strategyName}
                type="text"
              />
              {isNameEmpty && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    marginBottom: "3px",
                  }}
                >
                  <Typography
                    align="left"
                    variant="caption"
                    style={{ color: "red" }}
                  >
                    Please enter name.
                  </Typography>
                </div>
              )}
            </Grid>
            <Grid item xs={6} style={{ marginTop: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <FormControlLabel
                  style={{ marginLeft: "0px", marginRight: "0px" }}
                  value="dinein"
                  control={<SwitchCommon />}
                  label={
                    <Typography variant="caption" color="textSecondary">
                      Dine In
                    </Typography>
                  }
                  name="dinein"
                  checked={
                    !_.isEmpty(deliveryTypesList)
                      ? deliveryTypesList.includes(DELIVERY_TYPE_DINEIN)
                      : false
                  }
                  labelPlacement="bottom"
                  onChange={handleSelectDeliveryTypes}
                />
                <FormControlLabel
                  style={{ marginLeft: "0px", marginRight: "0px" }}
                  value="delivery"
                  control={<SwitchCommon />}
                  label={
                    <Typography variant="caption" color="textSecondary">
                      Delivery
                    </Typography>
                  }
                  name="delivery"
                  checked={
                    !_.isEmpty(deliveryTypesList)
                      ? deliveryTypesList.includes(DeliveryTpe.DELIVERY)
                      : false
                  }
                  labelPlacement="bottom"
                  onChange={handleSelectDeliveryTypes}
                />
                <FormControlLabel
                  style={{ marginLeft: "0px", marginRight: "0px" }}
                  value="takeout"
                  name="takeout"
                  control={<SwitchCommon />}
                  label={
                    <Typography variant="caption" color="textSecondary">
                      Takeout
                    </Typography>
                  }
                  checked={
                    !_.isEmpty(deliveryTypesList)
                      ? deliveryTypesList.includes(DeliveryTpe.TAKEOUT)
                      : false
                  }
                  labelPlacement="bottom"
                  onChange={handleSelectDeliveryTypes}
                />
              </div>
              {_.isEqual(isDeliveryTypeEmpty, true) && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    marginBottom: "3px",
                  }}
                >
                  <Typography
                    align="left"
                    variant="caption"
                    style={{ color: "red" }}
                  >
                    Please select a least one delivery option.
                  </Typography>
                </div>
              )}
            </Grid>

            <Grid item xs={12} sm={6}></Grid>

            {/* Platforms - Autocomplete with Chips */}
            <Grid
              item
              xs={12}
              sm={6}
              style={{ marginTop: "12px", paddingRight: "8px" }}
            >
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Platforms
              </Typography>
              <Autocomplete
                multiple
                id="platform-selector"
                options={platformOptions}
                className={classes.autocomplete}
                value={selectedPlatforms}
                getOptionLabel={(option: PlatformOption) => option.label}
                onChange={handlePlatformChange}
                renderTags={(value: PlatformOption[], getTagProps) =>
                  value.map((option: PlatformOption, index: number) => (
                    <Chip
                      key={option.id}
                      variant="outlined"
                      label={option.label}
                      style={{
                        margin: 2,
                      }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Grid>

            {/* Order Channels - Autocomplete with Chips */}
            <Grid
              item
              xs={12}
              sm={6}
              style={{ marginTop: "12px", paddingLeft: "8px" }}
            >
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Order Channels
              </Typography>
              <Autocomplete
                multiple
                id="channel-selector"
                options={channelOptions}
                className={classes.autocomplete}
                value={selectedChannels}
                getOptionLabel={(option: ChannelOption) => option.label}
                onChange={handleChannelChange}
                renderTags={(value: ChannelOption[], getTagProps) =>
                  value.map((option: ChannelOption, index: number) => (
                    <Chip
                      key={option.id}
                      variant="outlined"
                      label={option.label}
                      style={{
                        margin: 2,
                      }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Grid>

            <Grid item xs={12} style={{ marginTop: "8px" }}>
              <AvailabilitySelection
                nodeData={availability}
                disabled={false}
                handleSubmit={handleSubmitAvailability}
                setAvailabilityErrorMessage={setAvailabilityErrorMessage}
                backgroundColor={"entity_background"}
              />
              {isAvailabilityEmpty && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    marginBottom: "3px",
                  }}
                >
                  <Typography
                    align="left"
                    variant="caption"
                    style={{ color: "red" }}
                  >
                    Please select availability.
                  </Typography>
                </div>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ marginBottom: "8px" }}>
          <ButtonCommon
            variant="contained"
            style={{ fontSize: 11, width: "100px", marginRight: 8 }}
            color="yellow"
            onClick={handleReset}
          >
            Cancel
          </ButtonCommon>
          <ButtonCommon
            variant="contained"
            style={{
              fontSize: 11,
              marginLeft: 4,
              width: "100px",
            }}
            disabled={handleDisable()}
            isLoadingPage={isLoadingButton}
            color={isEdit ? "orange" : isDuplicate ? "blue" : "green"}
            onClick={handleSubmit}
          >
            {isEdit ? "Save" : isDuplicate ? "Duplicate" : "Create"}
          </ButtonCommon>
        </DialogActions>
      </DialogCommonDefault>
    </>
  );
};

export default StrategyAddEditModal;
